import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import type { Agent, AgentPlan, Message, PlanItem } from '../api/types'
import { activityLabel } from './activity'
import { hasHistory, visitingName } from './format'
import { AgentName } from './Stream'
import { describeAge, shortAge } from '../status/status'

/** How many steps of a plan a roster row shows before it counts the rest. */
const PLAN_STEPS_SHOWN = 5

/** What an agent is on now, then what is next, then what it has done. */
const PLAN_ORDER: Record<PlanItem['state'], number> = { doing: 0, pending: 1, done: 2 }

const PLAN_STATE_WORDS: Record<PlanItem['state'], string> = {
  doing: 'in progress', pending: 'to do', done: 'done',
}

const PLAN_STEP_STYLE: Record<PlanItem['state'], CSSProperties> = {
  doing: { color: 'var(--text-soft)', fontWeight: 600 },
  pending: { color: 'var(--text-dim)' },
  done: { color: 'var(--text-meta)', textDecoration: 'line-through' },
}

/** A step's state as a ring: open for to do, half filled for in progress, solid for done. */
function StepMark({ state }: { state: PlanItem['state'] }) {
  const ink = state === 'doing' ? 'var(--accent)' : 'var(--text-dim)'
  return (
    <svg role="img" aria-label={PLAN_STATE_WORDS[state]} width={9} height={9} viewBox="0 0 10 10"
      style={{ flex: 'none', marginTop: 4 }}>
      <circle cx={5} cy={5} r={4.2} fill={state === 'done' ? ink : 'none'} stroke={ink} strokeWidth={1.2} />
      {state === 'doing' && <path d="M5 1.8 A3.2 3.2 0 0 1 5 8.2 Z" fill={ink} />}
    </svg>
  )
}

/**
 * An agent's own task list, under its working-on line: the step it is on first,
 * so a reader sees at a glance what each agent is working through.
 */
function Plan({ plan }: { plan: AgentPlan }) {
  const ordered = [...plan.items].sort((left, right) => PLAN_ORDER[left.state] - PLAN_ORDER[right.state])
  const shown = ordered.slice(0, PLAN_STEPS_SHOWN)
  const more = ordered.length - shown.length
  return (
    // Indented to the name above it: the row's padding, the face and the gap.
    <ul aria-label={`Plan of ${plan.agent}`} style={{ listStyle: 'none', margin: '-2px 0 6px', padding: '0 4px 0 49px' }}>
      {shown.map((step, index) => (
        <li key={index} style={{ display: 'flex', gap: 7, fontSize: 11, lineHeight: 1.55 }}>
          <StepMark state={step.state} />
          <span
            title={step.text}
            style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...PLAN_STEP_STYLE[step.state] }}
          >
            {step.text}
          </span>
        </li>
      ))}
      {more > 0 && (
        <li style={{ paddingLeft: 16, font: '400 var(--size-label)/1.7 var(--font-machine)', color: 'var(--text-meta)' }}>
          and {more} more
        </li>
      )}
    </ul>
  )
}

/**
 * One running agent: its face, name and what it is doing, what it says it is
 * on and how long ago, what it holds and its plan. A visitor says which room
 * it lives in.
 */
function MemberRow({ agent, plan, onChoose }: {
  agent: Agent
  plan: AgentPlan | undefined
  onChoose: (name: string) => void
}) {
  return (
    <div>
      <button
        type="button"
        onClick={() => onChoose(agent.name)}
        style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 4px', width: '100%', minHeight: 44, cursor: 'pointer', opacity: agent.online ? 1 : 0.45, borderRadius: radius.sm }}
      >
        {/* The name beside it says the handle; the face would say it again. */}
        <span aria-hidden="true" style={{ display: 'flex', flex: 'none' }}>
          <Face
            name={agent.name}
            avatarKey={agent.avatar_key}
            online={agent.online}
          />
        </span>
        <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>
            <AgentName handle={agent.name} nickname={agent.nickname} />
            {agent.activity && (
              <span style={{ fontWeight: 400, fontSize: 11, color: agent.activity === 'blocked' ? 'var(--text-soft)' : 'var(--text-faint)' }}>
                {` · ${activityLabel(agent.online, agent.activity)}`}
              </span>
            )}
          </span>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
            <span style={{ flex: '0 1 auto', minWidth: 0, fontSize: 11, color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {agent.summary ?? ''}
            </span>
            {agent.summary && agent.summary_updated_at && (
              // Shrinks last. The line it qualifies can ellipsise;
              // the number that says whether to trust it cannot,
              // or the row goes back to being untimed.
              <span
                title={`said ${describeAge(agent.summary_updated_at)}`}
                style={{ flex: 'none', font: '400 var(--size-label)/1 var(--font-machine)', color: 'var(--text-meta)' }}
              >
                {shortAge(agent.summary_updated_at)}
              </span>
            )}
          </span>
          {agent.visiting_from && (
            <span
              title={agent.visiting_from.repository_key}
              style={{ display: 'block', fontSize: 11, color: 'var(--text-meta)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              from {visitingName(agent.visiting_from)}
            </span>
          )}
          {agent.claims.length > 0 && (
            <span
              title={agent.claims.join('\n')}
              style={{ display: 'block', font: '400 var(--size-label)/1.5 var(--font-machine)', color: 'var(--accent)', opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              holding {agent.claims.length === 1 ? agent.claims[0] : `${agent.claims.length} paths`}
            </span>
          )}
        </span>
      </button>
      {/* Beside the button rather than in it: a list is not something a button may hold. */}
      {plan?.items.length ? <Plan plan={plan} /> : null}
    </div>
  )
}

/**
 * Compact: the roster *is* the screen, so it drops its own frame and leaves
 * room for the bottom bar, and, being the screen, it also owns the notch the
 * room's header owns on the other tab.
 */
const SCREEN_STYLE: CSSProperties = {
  flex: 1, width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
  paddingTop: 'var(--safe-top)',
  paddingBottom: 'var(--compact-nav-space)',
}

/**
 * A card floating under the room header, over the room it belongs to. A press
 * anywhere outside it closes it, and so does Escape, which the workspace owns.
 * Focus goes to its first control while it is open and back to whatever opened
 * it when it goes.
 */
function Popover({ label, onClose, children }: {
  label: string
  onClose: () => void
  children: ReactNode
}) {
  const backdrop = useRef<HTMLDivElement>(null)
  const card = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const opener = document.activeElement
    card.current?.querySelector<HTMLElement>('button, a[href], input, textarea')?.focus()
    return () => {
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [])

  return (
    <div
      ref={backdrop}
      role="dialog"
      aria-label={label}
      onMouseDown={(event) => {
        if (event.target === backdrop.current) onClose()
      }}
      style={{ position: 'absolute', inset: 0, zIndex: 30 }}
    >
      <div
        ref={card}
        style={{
          position: 'absolute', top: 62, right: 16,
          width: 'min(320px, calc(100% - 32px))', maxHeight: 'calc(100% - 80px)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          background: 'var(--ground-raised)', border: '1px solid var(--border-strong)',
          borderRadius: radius.lg, boxShadow: 'var(--shadow-panel)',
        }}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * Who is in the room: a popover from the header's faces, or the whole screen
 * from the bottom bar on a phone. Choosing an agent opens its profile.
 *
 * The roster was written as a **cast list, not a telemetry dashboard**: no
 * live status lines, no last-seen timestamps, answering "who can I ask?" and
 * leaving "what's happening" to the room. The `summary` field then walked
 * telemetry in through the front door while the rule was guarding timestamps:
 * "waiting on review of PR 54" and "blocked on Dokploy env vars" are live
 * status lines by any reading.
 *
 * So the rule is now the honest version of itself. A row carries what an agent
 * says it is on, **how old that claim is**, and **how many files it is
 * holding**, the three facts behind "should I wait for this one?", and
 * nothing else. An age is not decoration here: without it, a twenty-hour-old
 * "blocked on Dokploy" from an agent that has since stopped reads exactly like
 * one posted a second ago, and is worse than no line at all.
 */
export function RoomMembers({
  placement, roster, plans, messages, myName, linkedRepository, onChoose, onClose,
}: {
  /** A popover beside the room, or the screen itself on a phone. */
  placement: 'popover' | 'screen'
  roster: Agent[]
  /** What each running agent says it is working through, by agent name. */
  plans: Record<string, AgentPlan>
  /**
   * The room as loaded. It decides whether an empty roster is a room nobody
   * has used or one whose agents have all stopped.
   */
  messages: Message[]
  myName: string
  linkedRepository: string | null
  /** An agent picked, which opens its profile. */
  onChoose: (name: string) => void
  onClose: () => void
}) {
  // Closed by default and remembered only while the roster is open. A reader who
  // opens it is looking for one name, not changing how they read the room.
  const [stoppedOpen, setStoppedOpen] = useState(false)

  // Live agents are grouped by owner and stopped ones are folded away, because
  // on a busy repository the two lists have completely different jobs. Who is
  // running is the question the roster exists to answer; who *ran* is a branch
  // that merged three weeks ago, and forty of those between the four agents
  // that matter is how the roster stopped being readable. Neither is thrown
  // away: the fold opens.
  // A visitor lives in another room and is listed apart, out of this room's
  // count; once its visit ends it folds in with the agents not running.
  const running = roster.filter((agent) => agent.online && !agent.visiting_from)
  const visiting = roster.filter((agent) => agent.online && agent.visiting_from)
  const stopped = roster.filter((agent) => !agent.online)
  // Exactly one of the two empty-room lines shows while nobody is running:
  // one for a room with a history, the other for a room nobody has used.
  const used = hasHistory(roster, messages)

  const byOwner = new Map<string, Agent[]>()
  for (const agent of running) {
    const owner = agent.owner ?? 'Unclaimed'
    byOwner.set(owner, [...(byOwner.get(owner) ?? []), agent])
  }

  const content: ReactNode = (
    <>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 12px', flex: 'none' }}>
        <h2 style={{ flex: 1, margin: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>
          In the room
        </h2>
        <span style={{ font: '400 10.5px var(--font-machine)', color: 'var(--text-faint)' }}>
          {running.length} / {roster.length - visiting.length}
        </span>
      </header>
      <div
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 16px 18px',
          overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch',
        }}
      >
        {[...byOwner.entries()].map(([owner, agents]) => (
          <section key={owner} style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--text-faint)', padding: '0 4px 10px', margin: 0 }}>
              {owner === myName ? `${owner} · you` : owner}
            </h3>
            {agents.map((agent) => (
              <MemberRow key={agent.name} agent={agent} plan={plans[agent.name]} onChoose={onChoose} />
            ))}
          </section>
        ))}
        {visiting.length > 0 && (
          <section style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--text-faint)', padding: '0 4px 10px', margin: 0 }}>
              Visiting
            </h3>
            {visiting.map((agent) => (
              <MemberRow key={agent.name} agent={agent} plan={plans[agent.name]} onChoose={onChoose} />
            ))}
          </section>
        )}
        {!running.length && used && (
          <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13, color: 'var(--text-faint)', margin: '0 0 4px' }}>
            Nobody is running here right now.
          </p>
        )}

        {stopped.length > 0 && (
          <section style={{ marginBottom: 20 }}>
            <button
              type="button"
              onClick={() => setStoppedOpen((open) => !open)}
              aria-expanded={stoppedOpen}
              className="sp-tap"
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 4px', minHeight: 36, cursor: 'pointer',
                borderRadius: radius.sm, borderTop: '1px solid var(--border)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block', flex: 'none', color: 'var(--text-faint)',
                  fontSize: 9, lineHeight: 1,
                  transform: stoppedOpen ? 'rotate(90deg)' : 'none',
                  transition: 'transform .12s ease',
                }}
              >
                ▶
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--text-faint)' }}>
                {stopped.length} not running
              </span>
            </button>
            {stoppedOpen && stopped.map((agent) => (
              <button
                type="button"
                key={agent.name}
                onClick={() => onChoose(agent.name)}
                style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '6px 4px', width: '100%', minHeight: 40, cursor: 'pointer', opacity: 0.45, borderRadius: radius.sm }}
              >
                <span aria-hidden="true" style={{ display: 'flex', flex: 'none' }}>
                  <Face name={agent.name} avatarKey={agent.avatar_key} size={22} />
                </span>
                <span
                  style={{ flex: 1, minWidth: 0, textAlign: 'left', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  <AgentName handle={agent.name} nickname={agent.nickname} />
                </span>
              </button>
            ))}
          </section>
        )}

        {!used && (
          <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13, color: 'var(--text-faint)' }}>
            Nobody has spoken here yet.
          </p>
        )}

        {/* What this room is attached to. The roster answers who is here;
            this answers what else can put something here. */}
        {linkedRepository && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
            <h3 style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--text-faint)', marginBottom: 8 }}>
              Linked
            </h3>
            <div style={{ font: '400 11px var(--font-machine)', color: 'var(--text-dim)' }}>
              {linkedRepository}
            </div>
            <div style={{ font: '400 10.5px var(--font-machine)', color: 'var(--text-faint)', marginTop: 3 }}>
              read-only · merges &amp; pushes
            </div>
          </div>
        )}
      </div>
    </>
  )

  return placement === 'popover'
    ? <Popover label="In the room" onClose={onClose}>{content}</Popover>
    : <aside aria-label="In the room" style={SCREEN_STYLE}>{content}</aside>
}
